import api from '../../../api/client';
// pages/EditSalePage.js
import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Table,
  Tag,
  message,
  Divider,
  Breadcrumb,
  Modal,
  Popconfirm,
  Statistic,
  Alert,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  HomeOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import config from "../../../config";

const { Option } = Select;
const { TextArea } = Input;

const EditSalePage = () => {
  const { id } = useParams();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sale, setSale] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm] = Form.useForm();
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Product type options
  const productTypes = [
    { value: "animals", label: "Animals", itemType: "animal" },
    { value: "poultry", label: "Poultry", itemType: "poultry" },
    { value: "eggs", label: "Eggs", itemType: "egg" },
    { value: "dairy", label: "Dairy", itemType: "dairy" },
    { value: "trays", label: "Trays", itemType: "general" },
    { value: "general", label: "General", itemType: "general" },
  ];

  // Payment methods
  const paymentMethods = [
    { value: "cash", label: "Cash" },
    { value: "mpesa", label: "M-Pesa" },
    { value: "bank", label: "Bank Transfer" },
    { value: "credit", label: "Credit" },
  ];

  // Payment statuses
  const paymentStatuses = [
    { value: "cash", label: "Cash" },
    { value: "unpaid", label: "Unpaid" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "fully_paid", label: "Fully Paid" },
  ];

  // Delivery methods
  const deliveryMethods = [
    { value: "delivered", label: "Delivered" },
    { value: "picked", label: "Picked Up" },
  ];

  // Fetch sale data
  const fetchSaleData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/unified/${id}`);
      const result = await response.json();

      if (result.success) {
        const saleData = result.data;
        setSale(saleData);
        setItems(saleData.items || []);

        // Set form values
        form.setFieldsValue({
          stakeholderId: saleData.stakeholderId,
          saleDate: moment(saleData.saleDate),
          deliveredMethod: saleData.deliveredMethod,
          deliveryPersonId: saleData.deliveryPersonId,
          paymentMethod: saleData.paymentMethod,
          paymentStatus: saleData.paymentStatus,
          totalAmount: saleData.totalAmount,
          notes: saleData.notes,
          dueDate: saleData.dueDate ? moment(saleData.dueDate) : null,
          confirmDate: saleData.confirmDate
            ? moment(saleData.confirmDate)
            : null,
          glDebit: saleData.glDebit || "1000",
          glCredit: saleData.glCredit || "4000",
          archive: saleData.archive || 0,
          invoiced: saleData.invoiced || 0,
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error fetching sale data:", error);
      setError("Failed to fetch sale data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaleData();
  }, [id]);

  // Calculate total amount from items
  const calculateTotalAmount = (itemsList) => {
    return itemsList.reduce((total, item) => total + (item.totalPrice || 0), 0);
  };

  // Update total amount when items change
  useEffect(() => {
    const totalAmount = calculateTotalAmount(items);
    form.setFieldsValue({ totalAmount });
  }, [items, form]);

  // Add new item
  const handleAddItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    setItemModalVisible(true);
  };

  // Edit existing item
  const handleEditItem = (item) => {
    setEditingItem(item);
    itemForm.setFieldsValue({
      productType: item.productType,
      itemType: item.itemType,
      tagNumber: item.tagNumber,
      species: item.species,
      category: item.category,
      quantity: item.quantity,
      weightKg: item.weightKg,
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice,
      eggType: item.eggType,
      traysCount: item.traysCount,
      brokenEggsCount: item.brokenEggsCount,
      milkType: item.milkType,
      liters: item.liters,
      productName: item.productName,
      unit: item.unit,
      description: item.description,
    });
    setItemModalVisible(true);
  };

  // Delete item
  const handleDeleteItem = (itemId) => {
    setItems(items.filter((item) => item.id !== itemId));
    message.success("Item deleted successfully");
  };

  // Save item (both new and edit)
  const handleSaveItem = (values) => {
    const itemData = {
      ...values,
      totalPrice: (values.quantity || 1) * (values.unitPrice || 0),
    };

    if (editingItem) {
      // Update existing item
      setItems(
        items.map((item) =>
          item.id === editingItem.id
            ? { ...item, ...itemData, id: editingItem.id }
            : item,
        ),
      );
      message.success("Item updated successfully");
    } else {
      // Add new item
      const newItem = {
        ...itemData,
        id: Date.now(), // Temporary ID for frontend
      };
      setItems([...items, newItem]);
      message.success("Item added successfully");
    }

    setItemModalVisible(false);
    setEditingItem(null);
    itemForm.resetFields();
  };

  // Cancel item editing
  const handleCancelItem = () => {
    setItemModalVisible(false);
    setEditingItem(null);
    itemForm.resetFields();
  };

  // Handle product type change
  const handleProductTypeChange = (productType) => {
    const productTypeConfig = productTypes.find(
      (pt) => pt.value === productType,
    );
    if (productTypeConfig) {
      itemForm.setFieldsValue({ itemType: productTypeConfig.itemType });
    }
  };

  // Calculate subtotal for item
  const calculateSubtotal = (values) => {
    const quantity = values.quantity || 1;
    const unitPrice = values.unitPrice || 0;
    return quantity * unitPrice;
  };

  // Update subtotal when quantity or unit price changes
  const handleItemValuesChange = (changedValues, allValues) => {
    if (
      changedValues.quantity !== undefined ||
      changedValues.unitPrice !== undefined
    ) {
      const subtotal = calculateSubtotal(allValues);
      itemForm.setFieldsValue({ totalPrice: subtotal });
    }
  };

  // Save sale
  const handleSaveSale = async (values) => {
    if (items.length === 0) {
      message.error("Please add at least one item to the sale");
      return;
    }

    setSaving(true);
    try {
      const saleData = {
        ...values,
        saleDate: values.saleDate.format("YYYY-MM-DD"),
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null,
        confirmDate: values.confirmDate
          ? values.confirmDate.format("YYYY-MM-DD")
          : null,
        totalAmount: calculateTotalAmount(items),
        items: items.map((item) => ({
          ...item,
          // Remove temporary ID for backend
          id: item.id && item.id > 1000000 ? undefined : item.id,
        })),
      };

      const response = await fetch(`/unified/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(saleData),
      });

      const result = await response.json();

      if (result.success) {
        message.success("Sale updated successfully");
        // navigate(`/sales/${id}`);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error updating sale:", error);
      message.error("Failed to update sale");
    } finally {
      setSaving(false);
    }
  };

  // Go back to sale details
  const handleGoBack = () => {
    // navigate(`/sales/${id}`);
  };

  // Columns for items table
  const itemColumns = [
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
      width: 120,
      render: (type) => <Tag color="blue">{type?.toUpperCase()}</Tag>,
    },
    {
      title: "Description",
      key: "description",
      render: (record) => getItemDescription(record),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (quantity, record) => getItemQuantityDisplay(record),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 120,
      render: (price) => `KES ${price?.toLocaleString()}`,
    },
    {
      title: "Subtotal",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      render: (price) => `KES ${price?.toLocaleString()}`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => handleEditItem(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDeleteItem(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <div>Loading sale data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "20px" }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Link to={`/transactions/sales/view/${id}`}>
              <Button size="small" onClick={handleGoBack}>
                Back to Sales
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "24px" }}
      >
        <Col>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: "24px" }}>Edit Sale - #{id}</h1>
              <p style={{ margin: 0, color: "#666" }}>
                Transaction: {sale?.transactionId}
              </p>
            </div>
          </div>
        </Col>
        <Col>
          <Space>
            <Link to={`/transactions/sales/view/${id}`}>
              <Button
                icon={<ArrowLeftOutlined />}
                // onClick={handleGoBack}
                size="small"
              >
                Back to Sale
              </Button>
            </Link>
            <Link to={`/transactions/sales/view/${id}`}>
              <Button size="small">Cancel</Button>
            </Link>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={() => form.submit()}
              size="small"
            >
              Save Changes
            </Button>
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveSale}
        initialValues={{
          glDebit: "1000",
          glCredit: "4000",
          archive: 0,
          invoiced: 0,
        }}
      >
        <Row gutter={[16, 16]}>
          {/* Sale Information */}
          <Col xs={24} lg={12}>
            <Card title="Sale Information">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Sale Date"
                    name="saleDate"
                    rules={[
                      { required: true, message: "Please select sale date" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Select date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Customer ID"
                    name="stakeholderId"
                    rules={[
                      { required: true, message: "Please enter customer ID" },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      placeholder="Customer ID"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Payment Method"
                    name="paymentMethod"
                    rules={[
                      {
                        required: true,
                        message: "Please select payment method",
                      },
                    ]}
                  >
                    <Select placeholder="Select payment method">
                      {paymentMethods.map((method) => (
                        <Option key={method.value} value={method.value}>
                          {method.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Payment Status"
                    name="paymentStatus"
                    rules={[
                      {
                        required: true,
                        message: "Please select payment status",
                      },
                    ]}
                  >
                    <Select placeholder="Select payment status">
                      {paymentStatuses.map((status) => (
                        <Option key={status.value} value={status.value}>
                          {status.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Delivery Method" name="deliveredMethod">
                    <Select placeholder="Select delivery method">
                      {deliveryMethods.map((method) => (
                        <Option key={method.value} value={method.value}>
                          {method.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Delivery Person ID" name="deliveryPersonId">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={1}
                      placeholder="Delivery Person ID"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Due Date" name="dueDate">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Select due date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Confirm Date" name="confirmDate">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="Select confirm date"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Notes" name="notes">
                <TextArea
                  rows={3}
                  placeholder="Additional notes about this sale..."
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Financial Information */}
          <Col xs={24} lg={12}>
            <Card title="Financial Information">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Total Amount" name="totalAmount">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                      formatter={(value) =>
                        `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/KES\s?|(,*)/g, "")}
                      placeholder="0.00"
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="GL Debit Account" name="glDebit">
                    <Input placeholder="1000" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="GL Credit Account" name="glCredit">
                    <Input placeholder="4000" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Archive Status" name="archive">
                    <Select>
                      <Option value={0}>Active</Option>
                      <Option value={1}>Archived</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Invoiced" name="invoiced">
                    <Select>
                      <Option value={0}>Not Invoiced</Option>
                      <Option value={1}>Invoiced</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* Statistics */}
              <Divider />
              <Row gutter={16}>
                <Col xs={12}>
                  <Statistic
                    title="Items Count"
                    value={items.length}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Total Amount"
                    value={calculateTotalAmount(items)}
                    precision={2}
                    valueStyle={{ color: "#3f8600" }}
                    prefix="KES"
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Sale Items */}
        <Card
          title={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Sale Items ({items.length})</span>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddItem}
              >
                Add Item
              </Button>
            </div>
          }
          style={{ marginTop: "16px" }}
        >
          {items.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#999" }}
            >
              <ExclamationCircleOutlined
                style={{ fontSize: "48px", marginBottom: "16px" }}
              />
              <p>
                No items added to this sale. Click "Add Item" to get started.
              </p>
            </div>
          ) : (
            <Table
              dataSource={items}
              columns={itemColumns}
              pagination={false}
              size="middle"
              scroll={{ x: 800 }}
              rowKey="id"
            />
          )}
        </Card>
      </Form>

      {/* Action Buttons at Bottom */}
      <Divider />
      <Row justify="center" style={{ marginTop: "24px" }}>
        <Space>
          <Button onClick={handleGoBack} size="small">
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={() => form.submit()}
            size="small"
          >
            Save Changes
          </Button>
        </Space>
      </Row>

      {/* Item Modal */}
      <Modal
        title={editingItem ? "Edit Item" : "Add New Item"}
        visible={itemModalVisible}
        onCancel={handleCancelItem}
        footer={[
          <Button key="cancel" onClick={handleCancelItem}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={() => itemForm.submit()}>
            {editingItem ? "Update Item" : "Add Item"}
          </Button>,
        ]}
        width={700}
      >
        <Form
          form={itemForm}
          layout="vertical"
          onFinish={handleSaveItem}
          onValuesChange={handleItemValuesChange}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Product Type"
                name="productType"
                rules={[
                  { required: true, message: "Please select product type" },
                ]}
              >
                <Select
                  placeholder="Select product type"
                  onChange={handleProductTypeChange}
                >
                  {productTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Item Type" name="itemType">
                <Input placeholder="Auto-filled" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Tag Number/Identifier" name="tagNumber">
                <Input placeholder="e.g., GT-123" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Species" name="species">
                <Input placeholder="e.g., goats, chicken" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Category" name="category">
                <Input placeholder="e.g., adult, weaner" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, message: "Please enter quantity" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  placeholder="1"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Dynamic fields based on product type */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Weight (kg)" name="weightKg">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.1}
                  placeholder="0.0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Unit Price (KES)"
                name="unitPrice"
                rules={[{ required: true, message: "Please enter unit price" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  formatter={(value) =>
                    `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/KES\s?|(,*)/g, "")}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Total Price (KES)" name="totalPrice">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  formatter={(value) =>
                    `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/KES\s?|(,*)/g, "")}
                  placeholder="0.00"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Product Name" name="productName">
                <Input placeholder="Product name" />
              </Form.Item>
            </Col>
          </Row>

          {/* Egg specific fields */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Egg Type" name="eggType">
                <Select placeholder="Select egg type">
                  <Option value="normal">Normal</Option>
                  <Option value="double_yolk">Double Yolk</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="broken">Broken</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Trays Count" name="traysCount">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Dairy specific fields */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Milk Type" name="milkType">
                <Input placeholder="e.g., cow, goat" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Liters" name="liters">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.1}
                  placeholder="0.0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Item description..." />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// Helper functions
const getItemDescription = (item) => {
  if (item.productName) return item.productName;
  switch (item.productType) {
    case "animals":
      return (
        `${item.species || ""} ${item.category || ""}`.trim().toUpperCase() ||
        "ANIMAL"
      );
    case "poultry":
      return item.species ? `${item.species} BIRDS` : "POULTRY";
    case "eggs":
      return `${item.eggType || "NORMAL"} EGGS`;
    case "dairy":
      return `${item.milkType || "MILK"} DAIRY`;
    case "trays":
      return "EGG TRAYS";
    default:
      return "GENERAL ITEM";
  }
};

const getItemQuantityDisplay = (item) => {
  if (item.quantity) return item.quantity.toString();
  switch (item.productType) {
    case "animals":
    case "poultry":
      return item.weightKg ? `${item.weightKg}kg` : "1";
    case "eggs":
      return `${item.traysCount || 0} trays`;
    case "dairy":
      return `${item.liters || 0}L`;
    case "trays":
      return `${item.traysCount || 0}pcs`;
    default:
      return "1";
  }
};

export default EditSalePage;
